import React, { useRef, useState } from 'react';

import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import { useLocation, useParams } from 'react-router';

import { Container } from '@uncinc/uncinc-react-kitchen-sink';
import { CoreLink, RecursiveMenu } from '@uncinc/react-drupal-core';
import type { MenuItemLinkElementProps } from '@uncinc/react-drupal-core/dist/Components/RecursiveMenu/types';

import EmailAccountPage from './Email';
import OverviewAccountPage from './Overview';
import PersonalAccountPage from './Personal';
import AddressesAccountPage from './Addresses';
import OrdersAccountPage from './Orders';
import ProductsAccountPage from './Products';
import { Hamburger } from '../../../components';
import { APP_OPTIONS, DEFAULT_LANGUAGE } from '../../../config/constants';
import { ROUTE_USER_LOGOUT } from '../../../config/routes';
import useCurrentUser from '../../../hooks/useCurrentUser';

import './style.scss';

const SideBarLinkElement: React.FC<MenuItemLinkElementProps> = ({
  title,
  url,
  onClickFunction,
}) => {
  const { pathname } = useLocation();
  const className = classNames('RecursiveMenu__title', {
    hasLink: Boolean(url),
    is_active: pathname.startsWith(url),
  });

  // Logout requires a hard navigation to let the backend handle session teardown.
  if (url === ROUTE_USER_LOGOUT) {
    return (
      <a href={ROUTE_USER_LOGOUT} className={className}>
        {title}
      </a>
    );
  }

  return (
    <CoreLink href={url} className={className} onClick={onClickFunction}>
      {title}
    </CoreLink>
  );
};

const UserAccountPage: React.FC = () => {
  const user = useCurrentUser();
  const { t } = useTranslation();
  const { tab, id, subpage } = useParams<{
    tab: string;
    id?: string;
    subpage?: string;
  }>();

  const { profile } = user ?? {};
  const {
    field_first_name: fieldFirstName = null,
    field_last_name: fieldLastName = null,
  } = profile ?? {};

  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const menuUsernameRef = useRef<HTMLDivElement>(null);
  const [mobileMenuIsOpen, setMobileMenuIsOpen] = useState(false);

  const toggleMobileMenuIsOpen = () => {
    mobileMenuRef.current?.classList.toggle('menuIsOpen');
    menuUsernameRef.current?.classList.toggle('menuIsOpen');
    setMobileMenuIsOpen((state) => !state);
  };

  const {
    accountPagePersonalTab,
    accountPageOverviewTab,
    accountPageEmailTab,
    accountPageAddressesTab,
    accountPageOrdersTab,
    accountPageProductsTab,
  } = APP_OPTIONS;

  const tabConfig = {
    [accountPagePersonalTab]: {
      icon: 'icon-user',
      component: PersonalAccountPage,
      title: t('mijn_vnl.personal.title'),
    },
    [accountPageOverviewTab]: {
      icon: 'icon-contact',
      component: OverviewAccountPage,
      title: t('mijn_vnl.overview.title'),
    },
    [accountPageEmailTab]: {
      icon: 'icon-mail-thin',
      component: EmailAccountPage,
      title: t('mijn_vnl.email.title'),
      subtitle: t('mijn_vnl.email.subtitle'),
    },
    [accountPageAddressesTab]: {
      icon: 'icon-home',
      component: AddressesAccountPage,
      title: t('mijn_vnl.addresses.title'),
    },
    [accountPageOrdersTab]: {
      icon: 'icon-shopping-cart',
      component: OrdersAccountPage,
      title: t('mijn_vnl.orders.title'),
    },
    [accountPageProductsTab]: {
      icon: 'icon-grid',
      component: ProductsAccountPage,
      title: t('mijn_vnl.products.title'),
    },
  };

  const InnerComponent = tabConfig[tab]!.component;

  // Pass orderId and subpage props to Orders component when on a detail page
  const componentProps =
    tab === accountPageOrdersTab && id ? { orderId: id, subpage } : {};

  // Hide title on order detail and licenses pages (they have their own headers)
  const isOrderDetailPage = tab === accountPageOrdersTab && id;

  return (
    <>
      <Helmet htmlAttributes={{ lang: DEFAULT_LANGUAGE }}>
        <title>{tabConfig[tab]?.title}</title>
      </Helmet>
      <div className="Relative">
        <div className="GreenFiller" />
        <Container className="AccountPage">
          <div className="AccountPage__desktopSidebar col-4">
            <div className="AccountPage__desktopSidebar__wrapper">
              <RecursiveMenu
                menuId="account"
                MenuItemLinkElement={SideBarLinkElement}
                navProps={{ 'aria-label': t('mijn_vnl.navigation_aria_label') }}
              />
            </div>
          </div>
          <div className="AccountPage__mobileSidebar col-12">
            <div
              ref={menuUsernameRef}
              className="AccountPage__mobileSidebar__username"
            >
              <Hamburger
                setShowCartPopup={false}
                OpenMobileMenu={mobileMenuIsOpen}
                handleClick={toggleMobileMenuIsOpen}
              />
              {user && fieldFirstName && (
                <span>
                  {fieldFirstName[0]?.value}{' '}
                  {fieldLastName && fieldLastName[0]?.value
                    ? fieldLastName[0].value
                    : ''}
                </span>
              )}
            </div>
            <div
              ref={mobileMenuRef}
              className="AccountPage__mobileSidebar__menuWrapper"
            >
              <RecursiveMenu
                menuId="account"
                onClickFunction={toggleMobileMenuIsOpen}
                MenuItemLinkElement={SideBarLinkElement}
                navProps={{ 'aria-label': t('mijn_vnl.navigation_aria_label') }}
              />
            </div>
          </div>
          <div
            className={classNames('AccountPage__inner col-12 col-lg-8', {
              'AccountPage__inner--hide-title': isOrderDetailPage,
            })}
          >
            <div className="AccountPage__inner__title">
              <div className="AccountPage__inner__title__icon">
                <i
                  className={classNames(
                    'icon icon-variant-1',
                    tabConfig[tab]?.icon,
                  )}
                />
              </div>
              <h1>{tabConfig[tab]?.title}</h1>
            </div>
            {tabConfig[tab]?.subtitle && (
              <div className="AccountPage__inner__subtitle">
                {tabConfig[tab]?.subtitle}
              </div>
            )}
            <InnerComponent {...componentProps} />
          </div>
        </Container>
        <div className="WhiteFiller" />
      </div>
    </>
  );
};

export default React.memo(UserAccountPage);
